/**
 * Block Service
 * Handles block-related operations and utilities
 */
export default class BlockService {

  /**
   * Load blocks for a farm
   * @param {Object} firebase - Firebase instance
   * @param {string} farmId - Farm ID
   * @returns {Promise<Array>} Array of blocks
   */
  async loadBlocks(firebase, farmId) {
    try {
      // Load blocks from Firestore
      const blocksSnapshot = await firebase.firestore()
        .collection('blocks')
        .where('farmId', '==', farmId)
        .get()
      
      const blocks = []
      blocksSnapshot.forEach(doc => {
        const block = doc.data()
        block.id = doc.id
        blocks.push(block)
      })
      
      console.log('Loaded', blocks.length, 'blocks')
      return blocks
    } catch (error) {
      console.error('Error loading blocks:', error)
      throw error
    }
  }

  /**
   * Load blocks GeoJSON from Firebase Storage
   * @param {Object} firebase - Firebase instance
   * @param {string} blocksGeoJsonPath - Path to GeoJSON file
   * @returns {Promise<Object>} GeoJSON data
   */
  async loadBlocksGeoJson(firebase, blocksGeoJsonPath) {
    try {
      const blocksGeoJsonStorageRef = firebase.storage().ref(blocksGeoJsonPath)
      const blocksGeoJsonUrl = await blocksGeoJsonStorageRef.getDownloadURL()
      const response = await fetch(blocksGeoJsonUrl)
      const geoJson = await response.json()
      console.log('Loaded blocks GeoJSON with', geoJson.features.length, 'features')
      return geoJson
    } catch (error) {
      console.error('Error loading blocks GeoJSON:', error)
      throw error
    }
  }

  /**
   * Find block by ID
   * @param {Array} blocks - Array of blocks
   * @param {string} blockId - Block ID to find
   * @returns {Object|null} Found block or null
   */
  findBlockById(blocks, blockId) {
    return blocks.find(block => block.id === blockId) || null
  }

  /**
   * Find block feature in GeoJSON by block ID
   * @param {Object} blocksGeoJson - Blocks GeoJSON data
   * @param {string} blockId - Block ID to find
   * @returns {Object|null} Found block feature or null
   */
  findBlockFeature(blocksGeoJson, blockId) {
    if (!blocksGeoJson || !blocksGeoJson.features) {
      return null
    }
    return blocksGeoJson.features.find(feature => feature.properties.blockId === blockId) || null
  }

  /**
   * Format block area for display
   * @param {number} area - Area in square meters
   * @returns {string} Formatted area string
   */
  formatArea(area) {
    if (!area) return 'N/A'
    
    // Convert to acres if in square meters
    if (area > 1000) {
      const acres = area * 0.000247105
      return `${acres.toFixed(2)} acres`
    }
    
    return `${area.toFixed(2)} sq m`
  }

  /**
   * Validate block geometry
   * @param {Object} geometry - Block geometry
   * @returns {boolean} True if valid, false otherwise
   */
  validateBlockGeometry(geometry) {
    if (!geometry || !geometry.coordinates) {
      return false
    }

    try {
      if (geometry.type === 'Polygon') {
        const coords = geometry.coordinates[0]
        return coords && coords.length >= 3 && coords.every(coord => 
          coord && coord.length === 2 && 
          typeof coord[0] === 'number' && typeof coord[1] === 'number' &&
          !isNaN(coord[0]) && !isNaN(coord[1])
        )
      }
      return false
    } catch (error) {
      console.error('Error validating block geometry:', error)
      return false
    }
  }

  /**
   * Calculate block center coordinates
   * @param {Object} geometry - Block geometry
   * @returns {Array} Center coordinates [lng, lat]
   */
  calculateBlockCenter(geometry) {
    try {
      if (geometry.type === 'Polygon' && geometry.coordinates && geometry.coordinates[0]) {
        const coords = geometry.coordinates[0]
        const centerLon = coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length
        const centerLat = coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length
        return [centerLon, centerLat]
      }
      return [0, 0] // Default fallback
    } catch (error) {
      console.error('Error calculating block center:', error)
      return [0, 0]
    }
  }

  /**
   * Get block statistics
   * @param {Array} blocks - Array of blocks
   * @returns {Object} Block statistics
   */
  getBlockStatistics(blocks) {
    const stats = {
      totalBlocks: blocks.length,
      totalArea: 0,
      averageArea: 0,
      largestBlock: null,
      smallestBlock: null
    }

    if (blocks.length === 0) {
      return stats
    }

    let totalArea = 0
    let largestBlock = blocks[0]
    let smallestBlock = blocks[0]

    blocks.forEach(block => {
      if (block.area) {
        totalArea += block.area
        
        if (block.area > largestBlock.area) {
          largestBlock = block
        }
        
        if (block.area < smallestBlock.area) {
          smallestBlock = block
        }
      }
    })

    stats.totalArea = totalArea
    stats.averageArea = totalArea / blocks.length
    stats.largestBlock = largestBlock
    stats.smallestBlock = smallestBlock

    return stats
  }
}
